import type { IStoreSetupStatus } from '@/interfaces/IStoreSetup';

/**
 * Pure rules of «¡Su tienda está en línea!» (plan §4.9). The variant comes
 * from the setup state, never from the (possibly stale) branch.
 */

export type TLiveVariant = 'noMenu' | 'menuOnly' | 'orders';

export function liveVariant(status: IStoreSetupStatus): TLiveVariant {
  if (status.menu.visibleItems === 0) return 'noMenu';
  if (status.setup?.ordersChoice?.menuOnly) return 'menuOnly';
  return 'orders';
}

export interface ILiveHeroKeys {
  titleKey: string;
  subtitleKey: string;
}

export function liveHeroKeys(variant: TLiveVariant): ILiveHeroKeys {
  switch (variant) {
    case 'noMenu':
      return {
        titleKey: 'storeSetup.live.noMenuTitle',
        subtitleKey: 'storeSetup.live.noMenuSubtitle',
      };
    case 'menuOnly':
      return {
        titleKey: 'storeSetup.live.title',
        subtitleKey: 'storeSetup.live.subtitleMenuOnly',
      };
    case 'orders':
      return {
        titleKey: 'storeSetup.live.title',
        subtitleKey: 'storeSetup.live.subtitle',
      };
  }
}

/**
 * The diner-facing WhatsApp text: «pedir online» only when orders are on;
 * the `.voseo` twin for Spanish in AR/UY (`usesMenuQrVoseo`).
 */
export function liveShareTextKey(
  variant: TLiveVariant,
  voseo: boolean
): string {
  if (variant === 'orders')
    return voseo
      ? 'storeSetup.live.shareText.voseo'
      : 'storeSetup.live.shareText';
  return voseo
    ? 'storeSetup.live.shareTextMenuOnly.voseo'
    : 'storeSetup.live.shareTextMenuOnly';
}

/** The plan fields the trial line reads (an `IRestaurant` fits). */
export interface ITrialStore {
  paidTier: number;
  planExpiresAt?: Date | string | null;
}

/** «Tiene 1 mes gratis del plan Plus»: Plus (tier 1) with an expiry still ahead. */
export function hasActiveTrial(store: ITrialStore, now: Date): boolean {
  if (store.paidTier !== 1 || !store.planExpiresAt) return false;
  const expiresAt = new Date(store.planExpiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt > now.getTime();
}
